/**
 * transferLogic — pure payload + vacancy-math builders from Angular
 * `DEEComponent`.
 *
 * These reproduce the exact request shapes and numeric logic of:
 *   - handleConfirm()            -> buildConfirmPayload()
 *   - techTransferVacInsert()    -> buildTechTransferReq()
 *   - TransferVacAddSubFunc()    -> buildTransferVacAddSubData()
 *   - postTransferLog()          -> buildTransferLog()
 *   - vacancyLog()               -> buildVacancyLog()
 *
 * The vacancy math (CounsellingType / ResultVacYn / vacNeed branches) is copied
 * verbatim. Do NOT simplify — the +1 / -1 arithmetic decides vacancy counts.
 */

import { CounsellingRecord } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */

const num = (v: any): number => Number(v);
const str = (v: any): string =>
  v === null || v === undefined ? '' : String(v);

export interface TransferContext {
  startEndRow: CounsellingRecord;
  clickSelectRow: any;
  /** rowSelect.data — the chosen destination vacancy row. */
  rowSelectData: any;
  confirmRow: any;
  techVacDetails: any;
  vacNeed: any;
  isScert: boolean;
  userName: any;
  relinguish?: any;
}

// ── handleConfirm payload (api/CnslTrnsferCUD via GetCounsellingTransfer) ─────

export function buildConfirmPayload(ctx: TransferContext) {
  const { startEndRow: se, clickSelectRow, rowSelectData, confirmRow } = ctx;

  const newDesignation =
    str(se.Designation) === '103' && str(rowSelectData?.office_type_id) === '1'
      ? '117'
      : str(se.Designation) === '103' &&
          str(rowSelectData?.office_type_id) === '3'
        ? '103'
        : str(se.CounsellingType) === '3'
          ? se.Designation
          : str(rowSelectData?.Subject) === '99'
            ? '117'
            : str(se.Directorate) === '6'
              ? rowSelectData?.Designation
              : clickSelectRow?.desgnation;

  return {
    records: {
      IndexId: confirmRow.IndexId,
      CounsellingId: confirmRow.counselling_id,
      SeniorityNumber: confirmRow.seniority_state,
      TeacherId: confirmRow.teacher_id,
      Designation: confirmRow.Designation,
      NewDesignation: newDesignation,
      Subject: confirmRow?.Subject,
      NewSubject: rowSelectData?.Subject,
      TeacherStatus: '1',
      RelinValue: null,
      SchoolKeyId: confirmRow.SchoolKeyId,
      NewSchoolKeyId: confirmRow.NewSchoolKeyId,
      NewSchoolName: confirmRow.new_sch_name,
      Panel: se?.PanelType?.id,
      ResultVacYn: confirmRow.ResultVacYn,
      VacancyNeedFlag: ctx.vacNeed,
      UpdtId: confirmRow.uptId,
      application_id: clickSelectRow?.application_id,
      district_id: clickSelectRow?.District,
      counselling_type: se.CounsellingType,
    },
  };
}

// ── techTransferVacInsert request (api/CnslVacNewCUD[ Aided ]) ────────────────

export function buildTechTransferReq(ctx: TransferContext) {
  const {
    startEndRow: se,
    clickSelectRow,
    rowSelectData,
    confirmRow,
    techVacDetails,
    isScert,
  } = ctx;

  let sbjt: any;

  if (str(se.CounsellingType) !== '3') {
    if (isScert) {
      sbjt = clickSelectRow.subject;
    } else {
      sbjt = !techVacDetails?.id
        ? str(se.SubjectId) === '26' || str(se.SubjectId) === '27'
          ? clickSelectRow.Post
            ? clickSelectRow.Post
            : str(se.Designation) === '103'
              ? str(clickSelectRow.desgnation) === '117'
                ? '99'
                : clickSelectRow.subject
              : se.SubjectId
          : str(se.Designation) === '103'
            ? str(clickSelectRow.desgnation) === '117'
              ? '99'
              : clickSelectRow.subject
            : se.SubjectId
        : str(se.Designation) === '103'
          ? str(clickSelectRow.desgnation) === '117'
            ? '99'
            : clickSelectRow.subject
          : null;
    }

    const vac =
      str(se.CounsellingType) === '2'
        ? str(confirmRow.ResultVacYn) === '1'
          ? num(techVacDetails?.vac)
            ? num(techVacDetails?.vac) + 1
            : 1
          : num(techVacDetails?.vac)
            ? num(techVacDetails?.vac)
            : 0
        : num(techVacDetails?.vac)
          ? num(techVacDetails?.vac) + 1
          : 1;

    return {
      records: [
        {
          IndexId: techVacDetails?.id ? techVacDetails?.id : null,
          Vac: vac,
          SchlKeyId: clickSelectRow?.school_key_id,
          Panel: !techVacDetails?.id ? se?.PanelType?.id : null,
          Desg: !techVacDetails?.id ? clickSelectRow?.desgnation : null,
          Sub: sbjt,
        },
        {
          IndexId: rowSelectData?.IndexId ? rowSelectData?.IndexId : null,
          Vac: num(rowSelectData?.vac) - 1,
          SchlKeyId: rowSelectData?.school_id,
        },
      ],
    };
  }

  // CounsellingType === '3'
  if (isScert) {
    sbjt = clickSelectRow.subject;
  } else {
    sbjt = !techVacDetails?.id
      ? str(se.SubjectId) === '26' || str(se.SubjectId) === '27'
        ? clickSelectRow.Post
          ? clickSelectRow.Post
          : se.SubjectId
        : se.SubjectId
      : null;
  }

  const vac3 =
    str(confirmRow.ResultVacYn) === '1'
      ? num(techVacDetails?.vac)
        ? num(techVacDetails?.vac) + 1
        : 1
      : num(techVacDetails?.vac);

  return {
    records: [
      {
        IndexId: techVacDetails?.id ? techVacDetails?.id : null,
        Vac: vac3,
        SchlKeyId: !techVacDetails?.id ? clickSelectRow?.school_key_id : null,
        Panel: !techVacDetails?.id ? se?.PanelType?.id : null,
        Desg: !techVacDetails?.id ? clickSelectRow.desgnation : null,
        Sub: sbjt,
      },
      {
        IndexId: rowSelectData?.IndexId ? rowSelectData?.IndexId : null,
        Vac: num(rowSelectData?.vac) - 1,
        SchlKeyId: rowSelectData?.school_id,
      },
    ],
  };
}

/** Returns the api endpoint alias for techTransferVacInsert (aided vs normal). */
export function techTransferEndpoint(
  startEndRow: CounsellingRecord
): 'aided' | 'normal' {
  return str(startEndRow.schType) === '2' ? 'aided' : 'normal';
}

// ── TransferVacAddSubFunc data (api/TransUpdtTchrVacList[2]) ───────────────────

export function buildTransferVacAddSubData(
  ctx: TransferContext,
  confirmPayload: any
) {
  const { startEndRow: se, clickSelectRow, rowSelectData } = ctx;
  const records = confirmPayload?.records;

  return {
    records: {
      VacSchlId: records?.NewSchoolKeyId,
      TchrSchlId: records?.SchoolKeyId,
      ResVac: str(se.CounsellingType) === '1' ? '3' : records?.ResultVacYn,
      Panel: records?.Panel,
      Desgn: se?.Designation,
      Sub:
        str(se.SubjectId) === '26' || str(se.SubjectId) === '27'
          ? rowSelectData?.Subject
            ? rowSelectData?.Subject
            : se.SubjectId
          : se.SubjectId,
      Post:
        str(se.SubjectId) === '26' || str(se.SubjectId) === '27'
          ? clickSelectRow.Post
            ? clickSelectRow.Post
            : se.SubjectId
          : se.SubjectId,
      DesgnTchr: clickSelectRow.desgnation,
      DepVac: str(ctx.vacNeed) === '2' ? num(rowSelectData?.vac) - 1 : null,
      DepNeed: str(ctx.vacNeed) === '1' ? num(rowSelectData?.need) - 1 : null,
      VacNeedFlag: ctx.vacNeed,
      Depid: rowSelectData?.IndexId,
      type: se.schType,
    },
  };
}

/** Endpoint alias for TransferVacAddSub (CounsellingType 1 uses the "2" variant). */
export function transferVacAddSubEndpoint(
  startEndRow: CounsellingRecord
): 'v2' | 'v1' {
  return str(startEndRow.CounsellingType) === '1' ? 'v2' : 'v1';
}

// ── postTransferLog (api/CnslTransferLog) ─────────────────────────────────────

export function buildTransferLog(
  ctx: TransferContext,
  rowObject: any,
  notWillAbsText: string
) {
  const { startEndRow: se, clickSelectRow, rowSelectData, confirmRow } = ctx;

  const teacherStatus =
    notWillAbsText === 'Relinguish' || notWillAbsText === 'Not Willing'
      ? '3'
      : notWillAbsText === 'Absent'
        ? '2'
        : notWillAbsText === 'Select' || notWillAbsText === 'Confirmation'
          ? '1'
          : '';

  return {
    records: {
      IndexId: null,
      CounsellingId: rowObject?.counselling_id
        ? rowObject?.counselling_id
        : confirmRow.counselling_id,
      SeniorityNumber: rowObject?.seniority_state
        ? rowObject?.seniority_state
        : confirmRow.seniority_state,
      TeacherId: rowObject?.teacher_id
        ? rowObject?.teacher_id
        : confirmRow.teacher_id,
      Designation: rowObject?.desgnation
        ? rowObject?.desgnation
        : confirmRow.Designation,
      NewDesignation:
        str(se?.CounsellingType) === '3'
          ? se?.Designation
          : str(rowSelectData?.Subject) === '99'
            ? '117'
            : str(se?.Directorate) === '6'
              ? rowSelectData?.Designation
              : clickSelectRow?.desgnation,
      Subject: rowObject?.subject ? rowObject?.subject : confirmRow?.Subject,
      NewSubject: se?.SubjectId ? se?.SubjectId : confirmRow?.Subject,
      SchoolKeyId: confirmRow?.SchoolKeyId ? confirmRow?.SchoolKeyId : null,
      NewSchoolKeyId: confirmRow?.NewSchoolKeyId
        ? confirmRow?.NewSchoolKeyId
        : null,
      TeacherStatus: teacherStatus,
      RelinValue: ctx.relinguish ? ctx.relinguish : null,
      VacancyNeedFlag: ctx.vacNeed ? ctx.vacNeed : null,
      User: ctx.userName ? ctx.userName : null,
      application_id: clickSelectRow?.application_id,
      district_id: clickSelectRow?.District,
    },
  };
}

// ── vacancyLog (api/CnslVacLog) — two records: old school + new school ────────

export function buildVacancyLog(ctx: TransferContext, rowObject: any) {
  const { startEndRow: se, rowSelectData, confirmRow, techVacDetails } = ctx;

  const oldVac =
    str(se?.CounsellingType) !== '1' && str(se?.CounsellingType) !== '3'
      ? str(se?.CounsellingType) === '2'
        ? str(confirmRow.ResultVacYn) === '1'
          ? num(techVacDetails?.vac)
            ? num(techVacDetails?.vac) + 1
            : 1
          : num(techVacDetails?.vac)
            ? num(techVacDetails?.vac)
            : 0
        : num(techVacDetails?.vac)
          ? num(techVacDetails?.vac) + 1
          : 1
      : str(se?.CounsellingType) === '3'
        ? techVacDetails?.id
          ? techVacDetails?.vac
          : null
        : rowSelectData?.vac
          ? rowSelectData?.vac
          : '';

  const newVac = rowSelectData?.vac
    ? str(se?.CounsellingType) === '1'
      ? str(ctx.vacNeed) === '2'
        ? num(rowSelectData?.vac) - 1
        : num(rowSelectData?.vac)
      : num(rowSelectData?.vac) - 1
    : '-';

  return {
    records: [
      {
        IndexId: null,
        SchlKeyId: confirmRow?.SchoolKeyId ? confirmRow?.SchoolKeyId : null,
        Panel: se.PanelType && se.PanelType.id,
        Desg: rowObject?.desgnation
          ? rowObject?.desgnation
          : confirmRow.Designation,
        Sub: rowObject?.subject ? rowObject?.subject : confirmRow?.Subject,
        Vac: oldVac,
        Need: str(se?.CounsellingType) === '1' ? rowSelectData?.need : null,
        User: ctx.userName,
      },
      {
        IndexId: null,
        SchlKeyId: confirmRow?.NewSchoolKeyId
          ? confirmRow?.NewSchoolKeyId
          : null,
        Panel: se.PanelType && se.PanelType.id,
        Desg: rowSelectData?.Designation ? rowSelectData?.Designation : null,
        Sub: se?.SubjectId ? se?.SubjectId : confirmRow?.Subject,
        Vac: newVac,
        Need:
          str(se?.CounsellingType) === '1'
            ? rowSelectData?.need && str(ctx.vacNeed) === '1'
              ? num(rowSelectData?.need) - 1
              : num(rowSelectData?.need)
            : null,
        User: ctx.userName,
      },
    ],
  };
}

// ── handleNotWillAbs payload (api/CnslTrnsferCUD) ─────────────────────────────

export function buildNotWillAbsPayload(
  rowObject: any,
  notWillAbsText: string,
  relinguish: any
) {
  if (notWillAbsText === 'Relinguish' || notWillAbsText === 'Not Willing') {
    return {
      records: {
        IndexId: rowObject?.IndexId,
        CounsellingId: rowObject?.counselling_id,
        SeniorityNumber: rowObject?.seniority_state,
        TeacherId: rowObject?.teacher_id,
        Designation: rowObject?.desgnation,
        NewDesignation: null,
        Subject: rowObject?.subject,
        NewSubject: null,
        SchoolKeyId: rowObject?.school_key_id,
        NewSchoolKeyId: null,
        TeacherStatus: '3',
        RelinValue: relinguish ? relinguish : '',
        UpdtId: rowObject?.uptId,
        application_id: rowObject?.application_id,
        district_id: rowObject?.District,
      },
    };
  }
  if (notWillAbsText === 'Absent') {
    return {
      records: {
        IndexId: rowObject?.IndexId,
        CounsellingId: rowObject?.counselling_id,
        SeniorityNumber: rowObject?.seniority_state,
        TeacherId: rowObject?.teacher_id,
        Designation: rowObject?.desgnation,
        NewDesignation: null,
        Subject: rowObject?.subject,
        NewSubject: null,
        SchoolKeyId: null,
        NewSchoolKeyId: null,
        TeacherStatus: '2',
        RelinValue: null,
        UpdtId: rowObject?.uptId,
        application_id: rowObject?.application_id,
        district_id: rowObject?.District,
      },
    };
  }
  // Willing (revert)
  return {
    records: {
      IndexId: rowObject.IndexId,
      IsActive: '0',
    },
  };
}

/* eslint-enable @typescript-eslint/no-explicit-any */
